// src/redux/hooks/auth.ts
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';
import type { ApiValidateAccountRequest2, ApiWebAuthRequest } from '@agunity/api-v4';

import { apiService } from '../../api/apiService';
import { globalInit, selectFilteredUsers, selectRequestedPath } from '../slices/global';
import { asyncLogin, asyncSelectUser, authenticateAccount, failure, flags } from '../slices/auth';
import { useAppDispatch, useAppSelector } from '../hooks';
import { DOMAIN_ID, ROOT_PATHS, NO_ACC_DOMAIN_ID, SUCCESS_STATUS, TARGET } from '../../Constants';

export const useLogin = () => {
  const dispatch = useAppDispatch();
  const messages = useIntl().formatMessage;
  const locale = useAppSelector(state => state.language.language);
  const [uname, setUname] = useState('');
  const [pass, setPass] = useState('');
  const [errorMessages, setErrorMessages] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgetPass, setForgetPass] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string>('');

  const isValidateEmail = (email: string) => {
    return Boolean(email.toLowerCase().match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    ));
  };

  useEffect(() => {
    if (uname && !isValidateEmail(uname)) {
      setEmailError(messages({ id: 'EMAIL_ERROR' }));
      return;
    }
    setEmailError('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uname]);

  const handleLogin = async (verification?: string | null) => {
    if (!isValidateEmail(uname)) {
      return;
    }
    setLoading(true);
    try {
      await dispatch(asyncLogin({ username: uname, password: pass, target: TARGET, verification } as ApiWebAuthRequest));
      await dispatch(globalInit(locale));
      const apiResponse = await apiService.featureV10FlagsList();
      if (apiResponse.status === SUCCESS_STATUS) {
        dispatch(flags(apiResponse.data));
      };
      return true;
    } catch (err) {
      const errorMessages: string = err instanceof Error ? messages({ id: 'INVALID_CREDS' }) : 'Something went wrong';
      dispatch(failure(errorMessages));
      setErrorMessages(errorMessages);
      setTimeout(() => setErrorMessages(''), 3000);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleForget = async (verification: string | null) => {
    setLoading(true);
    try {
      await dispatch(authenticateAccount({ emailAddress: uname, TemplateName: DOMAIN_ID, NoAccountTemplateName: NO_ACC_DOMAIN_ID, locale: locale, verification } as ApiValidateAccountRequest2));
      setForgetPass(false);
      setEmailSent(true);
      setErrorMessages(messages({ id: 'EMAIL_CHECK' }));
      return true;
    } catch (err) {
      setErrorMessages(messages({ id: 'EMAIL_WRONG' }));
      return false;
    } finally {
      setLoading(false);
      setTimeout(() => setErrorMessages(''), 3000);
    }
  };

  const toggleShowPassword = () => setShowPassword((show) => !show);

  return {
    uname,
    pass,
    setUname,
    setPass,
    errorMessages,
    showPassword,
    loading,
    handleLogin,
    handleForget,
    toggleShowPassword,
    setShowPassword,
    setErrorMessages,
    setLoading,
    forgetPass,
    setForgetPass,
    emailSent,
    setEmailSent,
    emailError,
    setEmailError
  };
};

export const useSelectUser = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const messages = useIntl().formatMessage;
  const [errorMessages, setErrorMessages] = useState('');
  const users = useAppSelector(selectFilteredUsers);
  const [selectedUserToken, setSelectedUserToken] = useState(apiService.userToken || '');
  const requestedPath: string = useAppSelector(selectRequestedPath) || ROOT_PATHS.ROOT;

  const handleSelectUser = async () => {
    if (selectedUserToken) {
      await dispatch(asyncSelectUser({ userToken: selectedUserToken }));
      navigate(requestedPath, { replace: true });
    } else {
      setErrorMessages(messages({ id: 'CHECK_USER_SELECTION' }));
    }
  };

  const handleChangeSelectUser = (event: SelectChangeEvent<string>) => {
    setSelectedUserToken(event.target.value as string);
  };

  return {
    errorMessages,
    handleSelectUser,
    handleChangeSelectUser,
    setSelectedUserToken,
    selectedUserToken,
    users,
    messages
  };
};